<template>
  <div class="tours-container">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap" rel="stylesheet">
    <div class="tours-hero" :style="{ backgroundImage: `url(${heroBackgroundImage})` }">
    </div>

    <div class="tours-content">
      <div class="tours-sidebar">
        <div class="filter-section">
          <h3>Cities</h3>
          <div class="filter-options">
            <router-link 
              v-for="city in cities" 
              :key="city.id"
              :to="`/destination/${city.id}`"
              class="filter-btn"
              :class="{ active: $route.params.city === city.id }"
            >
              {{ city.name }}
            </router-link>
          </div>
        </div>
      </div>

      <div class="tours-main">
        <!-- All Cities Overview -->
        <div class="cities-overview">
          <div class="tours-header">
            <h2>Yunnan's Beautiful Cities</h2>
            <p>Yunnan province is home to diverse landscapes, from tropical jungles to snow-capped mountains</p>
          </div>
          
          <div class="cities-grid">
            <router-link 
              v-for="city in cities" 
              :key="city.id" 
              :to="`/destination/${city.id}`"
              class="city-card"
            >
              <img :src="city.image" :alt="city.name">
              <div class="city-info">
                <h3>{{ city.name }}</h3>
                <p>{{ city.brief }}</p>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { cities } from '../data/cityData'

const route = useRoute()
const { proxy } = getCurrentInstance()

// Hero section content
const heroBackgroundImage = computed(() => '/images/cities/background.jpg')
const heroTitle = computed(() => 'Yunnan Cities')
const heroDescription = computed(() => 'Discover the diverse cultural landscapes and attractions of Yunnan\'s beautiful cities')

// Helper functions
function scrollToSection(sectionId) {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

onMounted(() => {
  proxy.$meta.update({
    title: 'Yunnan Destinations - Explore Top Places in Yunnan Province',
    description: 'Discover the most popular destinations in Yunnan, including Kunming, Dali, Lijiang, Shangri-La and more. Find your next adventure in Yunnan with our expert travel guides.',
    image: '/images/cities/background.jpg',
    canonicalUrl: 'https://realyunnan.com/destination/',
    ogTitle: 'Yunnan Destinations - Explore Top Places in Yunnan Province',
    ogDescription: 'Discover the most popular destinations in Yunnan, including Kunming, Dali, Lijiang, Shangri-La and more. Find your next adventure in Yunnan with our expert travel guides.',
    ogImage: '/images/cities/background.jpg',
    ogUrl: 'https://realyunnan.com/destination/'
  })
})

const destinationListJsonLd = computed(() => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Yunnan Destinations",
    "itemListElement": cities.map((city, idx) => ({
      "@type": "TouristDestination",
      "position": idx + 1,
      "name": city.name,
      "description": city.brief,
      "image": city.image,
      "url": `https://realyunnan.com/destination/${city.id}`
    }))
  });
})

useHead({
  script: [
    {
      type: 'application/ld+json',
      children: destinationListJsonLd
    }
  ]
})
</script>

<style scoped>
.tours-container {
  width: 100%;
  min-height: calc(100vh - 70px);
  background-color: var(--color-background-soft);
}

.tours-hero {
  width: 100%;
  min-height: 450px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 2rem;
}

.tours-content {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
}

.tours-sidebar {
  background: var(--color-background);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 20px;
}

.filter-section {
  margin-bottom: 2rem;
}

.filter-section h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: var(--color-heading);
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-btn {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  color: var(--color-text);
}

.filter-btn:hover {
  border-color: var(--primary-color);
}

.filter-btn.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.popular-tags h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: var(--color-heading);
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background: var(--color-background-soft);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag:hover {
  background: #e0e0e0;
  transform: translateY(-2px);
}

.tours-main {
  background: var(--color-background);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 1.5rem;
}

.tours-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
}

.tours-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--color-heading);
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--color-background-soft);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--color-text);
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: var(--color-border);
}

/* Cities Grid */
.cities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.city-card {
  background: var(--color-background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  cursor: pointer;
  transition: all 0.3s ease;
}

.city-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.city-card img {
  width: 100%;
  height: 240px;
  object-fit: cover;
}

.city-info {
  padding: 1rem;
}

.city-info h3 {
  margin: 0 0 0.5rem;
  color: var(--color-heading);
}

.city-info p {
  margin: 0;
  color: var(--color-text);
  font-size: 0.9rem;
}

/* City Detail */
.city-intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  
}

.city-description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--color-text);
}

.city-map img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.city-section {
  margin-bottom: 3rem;
}

.city-section h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--color-heading);
  position: relative;
}

.city-section h3::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 40px;
  height: 3px;
  background-color: #008c5c;
}

.city-section p {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: var(--color-text);
}

.attractions-grid, .minorities-grid, .food-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.attractions-grid {
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}

.attraction-card, .minority-card, .food-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
}

.attraction-card:hover, .minority-card:hover, .food-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.attraction-card img, .minority-card img, .food-card img {
  width: 100%;
  height: 180px;
  object-fit: cover;
}

.attraction-info, .minority-info, .food-info {
  padding: 1rem;
  background: var(--color-background);
}

.attraction-info h4, .minority-info h4, .food-info h4 {
  margin: 0;
  color: var(--color-heading);
  font-size: 1rem;
}

/* Tours List View */
.tours-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.tour-list-item {
  display: flex;
  background: var(--color-background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
}

.tour-list-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.tour-list-image {
  width: 200px;
  flex-shrink: 0;
}

.tour-list-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tour-list-info {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.tour-list-tags {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tour-list-footer {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tour-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text);
  font-size: 0.9rem;
}

.meta-item i {
  color: var(--primary-color);
}

.btn {
  display: inline-block;
  padding: 0.5rem 1.2rem;
  background-color: #008c5c;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn:hover {
  background-color: #00734b;
  transform: translateY(-3px);
}

.no-results {
  background: var(--color-background-soft);
  padding: 2rem;
  text-align: center;
  border-radius: 8px;
  color: var(--color-text);
}

@media (max-width: 1024px) {
  .tours-content {
    grid-template-columns: 1fr;
    padding: 0 1rem 1rem;
  }
  
  .tours-sidebar {
    position: static;
    margin-bottom: 1rem;
    grid-row: 1;
  }
  
  .filter-options {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .tour-list-item {
    flex-direction: column;
  }
  
  .tour-list-image {
    width: 100%;
    height: 200px;
  }
  
  .city-intro {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .tours-hero {
    min-height: 300px;
  }

  .tours-hero h1 {
    font-size: 1.8rem;
  }

  .tours-hero p {
    font-size: 1rem;
  }
  
  .tours-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .attractions-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }
  .minorities-grid, .food-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }

  .city-section {
    margin-bottom: 2rem;
  }

  .city-section h3 {
    font-size: 1.3rem;
  }

  .city-description {
    font-size: 1rem;
  }

  .tours-main, .tours-sidebar {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .tours-hero {
    min-height: 250px;
  }

  .tour-list-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .attractions-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  .minorities-grid, .food-grid {
    grid-template-columns: 1fr 1fr;
    gap: 0.8rem;
  }
  .city-section h3 {
    font-size: 1.2rem;
  }
  .city-description {
    font-size: 0.95rem;
  }
  .about-content {
    padding: 0 1rem 2rem;
  }
}
</style> 